/** @jsxImportSource @emotion/react */
import {
  EuiBadge,
  EuiButton,
  EuiDescriptionList,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiTextArea,
} from "@inscopix/ideas-eui";
import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { ToolSpecV2 } from "../ToolParamsGrid/ToolParamsGrid.types";
import { Maybe } from "graphql/_Types";
import moment from "moment";
import { css } from "@emotion/react";
import { useProjectDataContext } from "pages/project/ProjectDataProvider";

const TABLE_KEY_ILLEGAL_CHAR_REGEX = /[^A-Z0-9_]/g;
const TABLE_KEY_MAX_LENGTH = 10;

const styles = {
  detailList: css`
    min-height: 0;
    overflow-y: scroll;
    flex: 1 1 0;
    padding: 8px;
  `,
  tableForm: css`
    padding: 8px;
    background-color: white;
    border-top: 1px solid #e5e5e5;
  `,
};

interface WorkflowSelectionModalDetailProps {
  selectedToolSpecKey: string | undefined;
  toolSpecs: (Pick<
    ToolSpecV2,
    "key" | "name" | "help" | "version" | "toolkit" | "maturity"
  > & {
    credits: Maybe<number>;
    toolCreated: string;
    versionCreated: string;
    toolId: number;
  })[];
  isLoading: boolean;
  handleSubmit: (
    e: MouseEvent<HTMLButtonElement>,
    tableName: string,
    tableKey: string,
  ) => void;
  isKeyRequired?: boolean;
}

export const WorkflowSelectionModalDetail = ({
  selectedToolSpecKey,
  toolSpecs,
  isLoading,
  isKeyRequired = false,
  handleSubmit,
}: WorkflowSelectionModalDetailProps) => {
  const { analysisTableGroups } = useProjectDataContext();
  const selectedToolSpec = toolSpecs.find(
    (spec) => spec.key === selectedToolSpecKey,
  );
  const [tableName, setTableName] = useState<string>("");
  const [tableKey, setTableKey] = useState<string>("");

  const onChangeName = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setTableName(e.target.value);
  };

  const onChangeKey = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newKey = e.target.value
      .toUpperCase()
      .replace(TABLE_KEY_ILLEGAL_CHAR_REGEX, "")
      .substring(0, TABLE_KEY_MAX_LENGTH);
    setTableKey(newKey);
  };

  useEffect(() => {
    if (selectedToolSpec !== undefined) {
      let newAnalysisTableGroupName = selectedToolSpec.name;
      let suffix = 0;
      analysisTableGroups.forEach((group) => {
        if (group.toolId === selectedToolSpec.toolId) suffix++;
      });
      if (suffix !== 0) {
        newAnalysisTableGroupName = `${newAnalysisTableGroupName} (${suffix})`;
      }
      setTableName(newAnalysisTableGroupName);
    } else {
      setTableName("");
    }
  }, [analysisTableGroups, selectedToolSpec, selectedToolSpecKey]);

  if (selectedToolSpecKey === undefined) {
    return null;
  }

  if (selectedToolSpec === undefined) {
    return null;
  }

  const latestToolVersion = selectedToolSpec.version;
  const toolkit = selectedToolSpec.toolkit;
  const toolVersionDate = moment(selectedToolSpec.versionCreated).format(
    "YYYY-MM-DD",
  );
  const toolDate = moment(selectedToolSpec.toolCreated).format("YYYY-MM-DD");

  return (
    <EuiFlexGroup direction={"column"}>
      <EuiFlexItem css={styles.detailList}>
        <EuiDescriptionList
          textStyle="reverse"
          compressed
          listItems={[
            {
              title: "Latest Version",
              description: latestToolVersion,
            },
            {
              title: "Date Latest Version Published",
              description: toolVersionDate,
            },
            {
              title: "Date First Published",
              description: toolDate,
            },
            {
              title: "Toolkit",
              description: <EuiBadge color={"hollow"}>{toolkit}</EuiBadge>,
            },
          ]}
        />
      </EuiFlexItem>

      <EuiFlexItem grow={false} css={styles.tableForm}>
        <EuiFormRow label="Analysis table name">
          <EuiTextArea
            value={tableName}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChangeName(e)}
            resize={"none"}
            compressed
          />
        </EuiFormRow>
        {isKeyRequired && (
          <EuiFormRow label="Analysis table key">
            <EuiTextArea
              value={tableKey}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChangeKey(e)}
              resize={"none"}
              compressed
              required
            />
          </EuiFormRow>
        )}
        <EuiButton
          fill
          fullWidth
          onClick={(e: MouseEvent<HTMLButtonElement>) =>
            handleSubmit(e, tableName, tableKey)
          }
          isLoading={isLoading}
          disabled={
            tableName.trim() === "" || (isKeyRequired && tableKey.trim() === "")
          }
        >
          Create Table
        </EuiButton>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
